import { ComponentPropsWithoutRef, ComponentRef, CSSProperties, FC, ReactNode, Ref } from 'react';
import classNames from 'classnames';
import { Drawer } from 'vaul';

import { useRestoreFocus } from '../../useRestoreFocus';

import BottomSheetBody from './BottomSheetBody/BottomSheetBody';
import BottomSheetClose from './BottomSheetClose/BottomSheetClose';
import BottomSheetFooter from './BottomSheetFooter/BottomSheetFooter';
import BottomSheetHeader from './BottomSheetHeader/BottomSheetHeader';
import styles from './BottomSheet.module.css';

type BottomSheetContentPassthroughProps = Omit<ComponentPropsWithoutRef<typeof Drawer.Content>, 'className' | 'style'>;

export type BottomSheetProps = {
  /** Открыта ли шторка — управляется снаружи. */
  open: boolean;
  /** Вызывается при любой попытке открыть/закрыть (свайп вниз, клик снаружи, Escape, `BottomSheet.Close`). */
  onOpenChange: (open: boolean) => void;
  /** Содержимое: `BottomSheet.Close`, `BottomSheet.Header`, `BottomSheet.Body`, `BottomSheet.Footer`. */
  children: ReactNode;
  /** Высота поверх авто-фита под контент (в пределах max-height). */
  height?: number | string;
  /** Инлайн-стили шторки (контента). */
  style?: CSSProperties;
  /** Класс для оверлея-подложки. */
  overlayClassName?: string;
  /** Класс для самой шторки (контента). */
  contentClassName?: string;
  /** Можно ли закрыть шторку свайпом, кликом снаружи и Escape. При `false` ручка скрывается. По умолчанию `true`. */
  dismissible?: boolean;
  /** Показывать ли ручку у закрываемой шторки. Не меняет способы закрытия. По умолчанию `true`. */
  showHandle?: boolean;
  /** Ref на шторку (контент). */
  ref?: Ref<ComponentRef<typeof Drawer.Content>>;
} & BottomSheetContentPassthroughProps;

/** Выезжающая снизу шторка для мобайла на Vaul (обёртка над Radix Dialog). */
const BottomSheet: FC<BottomSheetProps> = ({
  open,
  onOpenChange,
  children,
  height,
  style,
  overlayClassName,
  contentClassName,
  dismissible = true,
  showHandle = true,
  ref,
  'aria-describedby': ariaDescribedBy,
  onOpenAutoFocus,
  onCloseAutoFocus,
  ...rest
}) => {
  const restoreFocus = useRestoreFocus(open);

  const handleOpenAutoFocus: NonNullable<BottomSheetContentPassthroughProps['onOpenAutoFocus']> = (event) => {
    onOpenAutoFocus?.(event);
    if (!event.defaultPrevented && event.target instanceof HTMLElement) {
      event.preventDefault();
      event.target.focus({ preventScroll: true });
    }
  };

  const handleCloseAutoFocus: NonNullable<BottomSheetContentPassthroughProps['onCloseAutoFocus']> = (event) => {
    onCloseAutoFocus?.(event);
    if (!event.defaultPrevented) restoreFocus();
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={onOpenChange}
      dismissible={dismissible}>
      <Drawer.Portal>
        <Drawer.Overlay className={classNames(styles.overlay, overlayClassName)} />
        <Drawer.Content
          ref={ref}
          aria-describedby={ariaDescribedBy}
          {...rest}
          onOpenAutoFocus={handleOpenAutoFocus}
          onCloseAutoFocus={handleCloseAutoFocus}
          className={classNames(styles.content, contentClassName)}
          style={{ ...style, height }}>
          {dismissible && showHandle && <Drawer.Handle className={styles.handle} />}
          {children}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

type BottomSheetCompound = typeof BottomSheet & {
  Header: typeof BottomSheetHeader;
  Body: typeof BottomSheetBody;
  Footer: typeof BottomSheetFooter;
  Close: typeof BottomSheetClose;
};

const CompoundBottomSheet = BottomSheet as BottomSheetCompound;
CompoundBottomSheet.Header = BottomSheetHeader;
CompoundBottomSheet.Body = BottomSheetBody;
CompoundBottomSheet.Footer = BottomSheetFooter;
CompoundBottomSheet.Close = BottomSheetClose;

export default CompoundBottomSheet;
